// WebSocket Data Layer for Real-Time Updates

import type { MarketData, Position, Signal, Trade } from '../types/trade';

// WebSocket configuration
const WS_BASE_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:8000/ws/activity';

// GAP-API-006: Read optional auth token from build-time env var.
// Set VITE_WS_TOKEN in .env (or docker-compose environment) to match
// the TRADEOS_TOOL_TOKEN value on the backend.
const _WS_TOKEN: string | undefined = import.meta.env.VITE_WS_TOKEN;

/** Append ?token=<value> to the URL when a token is configured. */
function _buildWsUrl(base: string): string {
  if (!_WS_TOKEN) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}token=${encodeURIComponent(_WS_TOKEN)}`;
}

// Throttling configuration
export const THROTTLE_INTERVAL_MS = 100; // 100ms batch buffer for high-frequency updates
const THROTTLED_MESSAGE_TYPES: Set<WSMessageType> = new Set([
  'market_data',
  'portfolio:update',
  'position:open',
  'position:close',
]);

// Message types - using colon-separated names matching the spec
export type WSMessageType =
  | 'portfolio:update'
  | 'position:open'
  | 'position:close'
  | 'system:status'
  | 'system:vpin'
  | 'system:regime'
  | 'activity:trade'
  | 'activity:signal'
  | 'activity:risk'
  | 'signal:new'
  | 'signal:modified'
  | 'market_data'
  | 'heartbeat'
  | 'subscribe'
  | 'unsubscribe';

// WebSocket message structure
export interface WSMessage<T = unknown> {
  type: WSMessageType;
  channel?: string;
  timestamp: string;
  data: T;
}

// Market data update
export interface MarketDataUpdate {
  symbol: string;
  price: number;
  bid: number;
  ask: number;
  volume: number;
  change24h: number;
  high24h: number;
  low24h: number;
}

// Position update
export interface PositionUpdate {
  positionId: string;
  symbol: string;
  side: 'long' | 'short';
  size: number;
  entryPrice: number;
  currentPrice: number;
  unrealizedPnl: number;
  unrealizedPnlPercent: number;
  marginUsed: number;
  liquidationPrice?: number;
  /** BUG-FIX-17: Present on position:close events — the final realized PnL. */
  realizedPnl?: number;
}

// Signal update
export interface SignalUpdate {
  signalId: string;
  symbol: string;
  type: 'entry' | 'exit' | 'adjust';
  direction: 'long' | 'short';
  confidence: number;
  price: number;
  reason: string;
  timestamp: string;
}

// Trade update
export interface TradeUpdate {
  tradeId: string;
  orderId: string;
  symbol: string;
  side: 'buy' | 'sell';
  type: 'market' | 'limit' | 'stop';
  quantity: number;
  price: number;
  status: 'pending' | 'filled' | 'partial' | 'cancelled' | 'rejected';
  timestamp: string;
  fees?: number;
}

// Portfolio update
export interface PortfolioUpdate {
  totalValue: number;
  cashBalance: number;
  positionsValue: number;
  unrealizedPnl: number;
  realizedPnl: number;
  marginUsed: number;
  marginAvailable: number;
  dailyPnl: number;
  dailyPnlPercent: number;
}

// Risk alert
export interface RiskAlert {
  alertId: string;
  type: 'margin_call' | 'drawdown' | 'position_limit' | 'daily_loss' | 'correlation';
  severity: 'info' | 'warning' | 'critical';
  message: string;
  details: Record<string, unknown>;
  timestamp: string;
}

// System status
export interface SystemStatus {
  status: 'online' | 'degraded' | 'offline' | 'maintenance';
  version: string;
  latency: number;
  connectedVenues: string[];
  timestamp: string;
}

// Subscription options
export interface SubscriptionOptions {
  channels: string[];
  symbols?: string[];
}

// Connection state
export type ConnectionState = 'connecting' | 'connected' | 'disconnecting' | 'disconnected' | 'error';

// Event handlers
export type MessageHandler<T = unknown> = (message: WSMessage<T>) => void;
export type ConnectionHandler = (state: ConnectionState) => void;
export type ErrorHandler = (error: Error) => void;

// Throttled message batch for high-frequency updates
interface ThrottledBatch {
  messages: WSMessage[];
  flushTimer: ReturnType<typeof setTimeout> | null;
}

// WebSocket client class
export class WebSocketClient {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private connectionTimeout: ReturnType<typeof setTimeout> | null = null;
  
  private messageHandlers: Map<WSMessageType, Set<MessageHandler>> = new Map();
  private connectionHandlers: Set<ConnectionHandler> = new Set();
  private errorHandlers: Set<ErrorHandler> = new Set();
  
  private connectionState: ConnectionState = 'disconnected';
  private subscriptions: Set<string> = new Set();
  
  // Throttling state - batch buffer for high-frequency updates
  private throttledBatches: Map<WSMessageType, ThrottledBatch> = new Map();
  
  constructor(private url: string = WS_BASE_URL) {}
  
  // Connect to WebSocket server
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }
      
      this.updateConnectionState('connecting');
      
      try {
        this.ws = new WebSocket(_buildWsUrl(this.url));
        
        // Connection timeout
        this.connectionTimeout = setTimeout(() => {
          if (this.connectionState === 'connecting') {
            this.ws?.close();
            reject(new Error('Connection timeout'));
          }
        }, 10000);
        
        this.ws.onopen = () => {
          clearTimeout(this.connectionTimeout!);
          this.reconnectAttempts = 0;
          this.updateConnectionState('connected');
          this.startHeartbeat();
          this.resubscribe();
          resolve();
        };
        
        this.ws.onclose = (event) => {
          clearTimeout(this.connectionTimeout!);
          this.stopHeartbeat();
          
          // Only reconnect on abnormal close (not clean close code 1000)
          // and when not explicitly disconnecting
          if (this.connectionState !== 'disconnecting' && event.code !== 1000) {
            this.updateConnectionState('disconnected');
            this.handleReconnect();
          } else {
            this.updateConnectionState('disconnected');
          }
        };
        
        this.ws.onerror = (error) => {
          clearTimeout(this.connectionTimeout!);
          this.updateConnectionState('error');
          this.notifyErrorHandlers(new Error('WebSocket error'));
          reject(error);
        };
        
        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };
      } catch (error) {
        this.updateConnectionState('error');
        reject(error);
      }
    });
  }
  
  // Disconnect from WebSocket server
  disconnect(): void {
    if (this.ws) {
      this.updateConnectionState('disconnecting');
      this.stopHeartbeat();
      this.flushAllBatches(); // Flush any pending throttled messages
      this.clearThrottlingTimers(); // Clear all throttling timers
      this.ws.close();
      this.ws = null;
    }
  }
  
  // Flush all pending throttled batches immediately
  private flushAllBatches(): void {
    this.throttledBatches.forEach((_, messageType) => {
      this.flushBatch(messageType);
    });
  }
  
  // Clear all throttling timers
  private clearThrottlingTimers(): void {
    this.throttledBatches.forEach((batch) => {
      if (batch.flushTimer) {
        clearTimeout(batch.flushTimer);
        batch.flushTimer = null;
      }
    });
    this.throttledBatches.clear();
  }
  
  // Subscribe to channels
  subscribe(options: SubscriptionOptions): void {
    const message: WSMessage<SubscriptionOptions> = {
      type: 'subscribe',
      timestamp: new Date().toISOString(),
      data: options,
    };
    
    options.channels.forEach(channel => this.subscriptions.add(channel));
    this.send(message);
  }
  
  // Unsubscribe from channels
  unsubscribe(channels: string[]): void {
    const message: WSMessage<{ channels: string[] }> = {
      type: 'unsubscribe',
      timestamp: new Date().toISOString(),
      data: { channels },
    };
    
    channels.forEach(channel => this.subscriptions.delete(channel));
    this.send(message);
  }
  
  // Send message
  private send<T>(message: WSMessage<T>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }
  
  // Handle incoming message with throttling for high-frequency updates
  private handleMessage(data: string): void {
    try {
      const message: WSMessage = JSON.parse(data);
      
      // Check if this message type should be throttled
      if (THROTTLED_MESSAGE_TYPES.has(message.type)) {
        this.handleThrottledMessage(message);
      } else {
        // Non-throttled messages are dispatched immediately
        this.dispatchMessage(message);
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
    }
  }
  
  // Handle throttled message - batch and flush after interval
  private handleThrottledMessage(message: WSMessage): void {
    const messageType = message.type;
    
    // Get or create batch for this message type
    let batch = this.throttledBatches.get(messageType);
    if (!batch) {
      batch = { messages: [], flushTimer: null };
      this.throttledBatches.set(messageType, batch);
    }
    
    // Add message to batch
    batch.messages.push(message);
    
    // If no timer is running, start one
    if (!batch.flushTimer) {
      batch.flushTimer = setTimeout(() => {
        this.flushBatch(messageType);
      }, THROTTLE_INTERVAL_MS);
    }
  }
  
  // Flush a batch of throttled messages
  private flushBatch(messageType: WSMessageType): void {
    const batch = this.throttledBatches.get(messageType);
    if (!batch) return;
    
    // Clear the timer
    batch.flushTimer = null;
    
    // Get the latest message for each unique key (e.g., symbol for market_data, positionId for positions)
    const latestMessages = this.deduplicateMessages(batch.messages, messageType);
    
    // Dispatch each deduplicated message
    latestMessages.forEach(message => {
      this.dispatchMessage(message);
    });
    
    // Clear the batch
    batch.messages = [];
  }
  
  // Deduplicate messages - keep only the latest for each unique key
  private deduplicateMessages(messages: WSMessage[], messageType: WSMessageType): WSMessage[] {
    const latestByKey = new Map<string, WSMessage>();
    
    messages.forEach(message => {
      // Determine the key based on message type
      let key: string;
      const data = message.data as Record<string, unknown>;
      
      switch (messageType) {
        case 'market_data':
          key = `market_data:${(data as unknown as MarketDataUpdate).symbol}`;
          break;
        case 'position:open':
        case 'position:close':
          key = `position:${(data as unknown as PositionUpdate).positionId}`;
          break;
        case 'portfolio:update':
          key = 'portfolio'; // Single portfolio
          break;
        default:
          key = `${messageType}:${message.timestamp}`;
      }
      
      // Always keep the latest message for each key
      latestByKey.set(key, message);
    });
    
    return Array.from(latestByKey.values());
  }
  
  // Dispatch a message to handlers
  private dispatchMessage(message: WSMessage): void {
    const handlers = this.messageHandlers.get(message.type);
    
    if (handlers) {
      handlers.forEach(handler => handler(message));
    }
    
    // Also notify generic handlers
    const genericHandlers = this.messageHandlers.get('*' as WSMessageType);
    if (genericHandlers) {
      genericHandlers.forEach(handler => handler(message));
    }
  }
  
  // Handle reconnection
  private handleReconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
      
      setTimeout(() => {
        this.connect().catch(() => {
          // Reconnect failed, will try again
        });
      }, delay);
    } else {
      this.notifyErrorHandlers(new Error('Max reconnection attempts reached'));
    }
  }
  
  // Resubscribe to channels after reconnect
  private resubscribe(): void {
    if (this.subscriptions.size > 0) {
      this.subscribe({
        channels: Array.from(this.subscriptions),
      });
    }
  }
  
  // Start heartbeat
  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      this.send({
        type: 'heartbeat',
        timestamp: new Date().toISOString(),
        data: {},
      });
    }, 30000);
  }
  
  // Stop heartbeat
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }
  
  // Update connection state
  private updateConnectionState(state: ConnectionState): void {
    this.connectionState = state;
    this.connectionHandlers.forEach(handler => handler(state));
  }
  
  // Notify error handlers
  private notifyErrorHandlers(error: Error): void {
    this.errorHandlers.forEach(handler => handler(error));
  }
  
  // Event subscription methods
  onMessage<T = unknown>(type: WSMessageType, handler: MessageHandler<T>): () => void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, new Set());
    }
    this.messageHandlers.get(type)!.add(handler as MessageHandler);
    
    return () => {
      this.messageHandlers.get(type)?.delete(handler as MessageHandler);
    };
  }
  
  onConnectionChange(handler: ConnectionHandler): () => void {
    this.connectionHandlers.add(handler);
    return () => {
      this.connectionHandlers.delete(handler);
    };
  }
  
  onError(handler: ErrorHandler): () => void {
    this.errorHandlers.add(handler);
    return () => {
      this.errorHandlers.delete(handler);
    };
  }
  
  // Get current connection state
  getConnectionState(): ConnectionState {
    return this.connectionState;
  }
  
  // Check if connected
  isConnected(): boolean {
    return this.connectionState === 'connected';
  }
}

// Singleton instance
let wsClient: WebSocketClient | null = null;

export function getWebSocketClient(): WebSocketClient {
  if (!wsClient) {
    wsClient = new WebSocketClient();
  }
  return wsClient;
}

// Reset the singleton instance (for testing)
export function resetWebSocketClient(): void {
  if (wsClient) {
    wsClient.disconnect();
    wsClient = null;
  }
}

// React hook for WebSocket
import { useEffect, useState, useCallback, useRef } from 'react';

export function useWebSocket() {
  const client = getWebSocketClient();
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  
  useEffect(() => {
    const unsubscribe = client.onConnectionChange(setConnectionState);
    return unsubscribe;
  }, [client]);
  
  const connect = useCallback(() => client.connect(), [client]);
  const disconnect = useCallback(() => client.disconnect(), [client]);
  
  return {
    client,
    connectionState,
    isConnected: connectionState === 'connected',
    connect,
    disconnect,
  };
}

// Hook for subscribing to market data
export function useMarketData(symbols: string[]) {
  const { client, isConnected } = useWebSocket();
  const [marketData, setMarketData] = useState<Map<string, MarketDataUpdate>>(new Map());
  
  useEffect(() => {
    if (!isConnected) return;
    
    const unsubscribe = client.onMessage<MarketDataUpdate>('market_data', (message) => {
      setMarketData(prev => {
        const next = new Map(prev);
        next.set(message.data.symbol, message.data);
        return next;
      });
    });
    
    client.subscribe({
      channels: ['market_data'],
      symbols,
    });
    
    return () => {
      unsubscribe();
      client.unsubscribe(['market_data']);
    };
  }, [client, isConnected, symbols.join(',')]);
  
  return marketData;
}

// Hook for position updates (handles both open and close events)
export function usePositionUpdates() {
  const { client, isConnected } = useWebSocket();
  const [positions, setPositions] = useState<Map<string, PositionUpdate>>(new Map());
  
  useEffect(() => {
    if (!isConnected) return;
    
    const unsubOpen = client.onMessage<PositionUpdate>('position:open', (message) => {
      setPositions(prev => {
        const next = new Map(prev);
        next.set(message.data.positionId, message.data);
        return next;
      });
    });
    
    const unsubClose = client.onMessage<PositionUpdate>('position:close', (message) => {
      setPositions(prev => {
        const next = new Map(prev);
        next.delete(message.data.positionId);
        return next;
      });
    });
    
    client.subscribe({ channels: ['positions'] });
    
    return () => {
      unsubOpen();
      unsubClose();
      client.unsubscribe(['positions']);
    };
  }, [client, isConnected]);
  
  return positions;
}

// Hook for signal updates (handles both new and modified signals)
export function useSignalUpdates() {
  const { client, isConnected } = useWebSocket();
  const [signals, setSignals] = useState<SignalUpdate[]>([]);
  
  useEffect(() => {
    if (!isConnected) return;
    
    const unsubNew = client.onMessage<SignalUpdate>('signal:new', (message) => {
      setSignals(prev => [message.data, ...prev].slice(0, 100));
    });
    
    const unsubModified = client.onMessage<SignalUpdate>('signal:modified', (message) => {
      setSignals(prev => [message.data, ...prev].slice(0, 100));
    });
    
    client.subscribe({ channels: ['signals'] });
    
    return () => {
      unsubNew();
      unsubModified();
      client.unsubscribe(['signals']);
    };
  }, [client, isConnected]);
  
  return signals;
}

// Hook for trade updates
export function useTradeUpdates() {
  const { client, isConnected } = useWebSocket();
  const [trades, setTrades] = useState<TradeUpdate[]>([]);
  
  useEffect(() => {
    if (!isConnected) return;
    
    const unsubscribe = client.onMessage<TradeUpdate>('activity:trade', (message) => {
      setTrades(prev => [message.data, ...prev].slice(0, 100));
    });
    
    client.subscribe({ channels: ['activity'] });
    
    return () => {
      unsubscribe();
      client.unsubscribe(['activity']);
    };
  }, [client, isConnected]);
  
  return trades;
}

// Hook for portfolio updates
export function usePortfolioUpdates() {
  const { client, isConnected } = useWebSocket();
  const [portfolio, setPortfolio] = useState<PortfolioUpdate | null>(null);
  
  useEffect(() => {
    if (!isConnected) return;
    
    const unsubscribe = client.onMessage<PortfolioUpdate>('portfolio:update', (message) => {
      setPortfolio(message.data);
    });
    
    client.subscribe({ channels: ['portfolio'] });
    
    return () => {
      unsubscribe();
      client.unsubscribe(['portfolio']);
    };
  }, [client, isConnected]);
  
  return portfolio;
}

// Hook for risk alerts
export function useRiskAlerts() {
  const { client, isConnected } = useWebSocket();
  const [alerts, setAlerts] = useState<RiskAlert[]>([]);
  
  useEffect(() => {
    if (!isConnected) return;
    
    const unsubscribe = client.onMessage<RiskAlert>('activity:risk', (message) => {
      setAlerts(prev => [message.data, ...prev].slice(0, 50));
    });
    
    client.subscribe({ channels: ['activity'] });
    
    return () => {
      unsubscribe();
      client.unsubscribe(['activity']);
    };
  }, [client, isConnected]);
  
  return alerts;
}

// Hook for system status
export function useSystemStatus() {
  const { client, isConnected } = useWebSocket();
  const [status, setStatus] = useState<SystemStatus | null>(null);
  
  useEffect(() => {
    if (!isConnected) return;
    
    const unsubscribe = client.onMessage<SystemStatus>('system:status', (message) => {
      setStatus(message.data);
    });
    
    client.subscribe({ channels: ['system'] });
    
    return () => {
      unsubscribe();
      client.unsubscribe(['system']);
    };
  }, [client, isConnected]);
  
  return status;
}

// React Query cache invalidation hook for WebSocket events
// This hook listens to WebSocket events and invalidates the relevant React Query caches
// to ensure the UI stays in sync with real-time updates
// 
// Usage: Call this hook at the app root level to enable automatic cache invalidation
// 
// Example:
//   import { useWebSocketCacheInvalidation } from './services/websocket';
//   
//   function App() {
//     useWebSocketCacheInvalidation();
//     return <Router>...</Router>;
//   }
//
export function useWebSocketCacheInvalidation() {
  const { client, isConnected } = useWebSocket();
  
  // We need to get the query client from context, but we can't import useQueryClient here
  // to avoid circular dependencies. Instead, we'll use a callback pattern that the app
  // can provide via the QueryClientProvider context.
  // 
  // The actual invalidation is handled by components that use the WebSocket hooks
  // and can access the query client via useQueryClient().
  // 
  // This hook is primarily for subscribing to channels and providing a central place
  // for cache invalidation logic. Components should use the specific hooks below
  // which include invalidation.
  
  useEffect(() => {
    if (!isConnected) return;
    
    // Subscribe to all relevant channels
    client.subscribe({ 
      channels: ['positions', 'portfolio', 'activity', 'signals', 'system'] 
    });
    
    return () => {
      client.unsubscribe(['positions', 'portfolio', 'activity', 'signals', 'system']);
    };
  }, [client, isConnected]);
}

// Hook that provides position updates with React Query cache invalidation
// Use this in components that need positions data to stay in sync
export function usePositionUpdatesWithInvalidation() {
  const { client, isConnected } = useWebSocket();
  const [positions, setPositions] = useState<Map<string, PositionUpdate>>(new Map());
  
  useEffect(() => {
    if (!isConnected) return;
    
    const unsubOpen = client.onMessage<PositionUpdate>('position:open', (message) => {
      setPositions(prev => {
        const next = new Map(prev);
        next.set(message.data.positionId, message.data);
        return next;
      });
      // Dispatch custom event for React Query invalidation
      window.dispatchEvent(new CustomEvent('ws:position:update'));
    });
    
    const unsubClose = client.onMessage<PositionUpdate>('position:close', (message) => {
      setPositions(prev => {
        const next = new Map(prev);
        next.delete(message.data.positionId);
        return next;
      });
      // Dispatch custom event for React Query invalidation
      window.dispatchEvent(new CustomEvent('ws:position:update'));
    });
    
    client.subscribe({ channels: ['positions'] });
    
    return () => {
      unsubOpen();
      unsubClose();
      client.unsubscribe(['positions']);
    };
  }, [client, isConnected]);
  
  return positions;
}

// Hook that provides portfolio updates with React Query cache invalidation
export function usePortfolioUpdatesWithInvalidation() {
  const { client, isConnected } = useWebSocket();
  const [portfolio, setPortfolio] = useState<PortfolioUpdate | null>(null);
  
  useEffect(() => {
    if (!isConnected) return;
    
    const unsubscribe = client.onMessage<PortfolioUpdate>('portfolio:update', (message) => {
      setPortfolio(message.data);
      // Dispatch custom event for React Query invalidation
      window.dispatchEvent(new CustomEvent('ws:portfolio:update'));
    });
    
    client.subscribe({ channels: ['portfolio'] });
    
    return () => {
      unsubscribe();
      client.unsubscribe(['portfolio']);
    };
  }, [client, isConnected]);
  
  return portfolio;
}

// Hook that provides trade updates with React Query cache invalidation
export function useTradeUpdatesWithInvalidation() {
  const { client, isConnected } = useWebSocket();
  const [trades, setTrades] = useState<TradeUpdate[]>([]);
  
  useEffect(() => {
    if (!isConnected) return;
    
    const unsubscribe = client.onMessage<TradeUpdate>('activity:trade', (message) => {
      setTrades(prev => [message.data, ...prev].slice(0, 100));
      // Dispatch custom event for React Query invalidation
      window.dispatchEvent(new CustomEvent('ws:trade:update'));
    });
    
    client.subscribe({ channels: ['activity'] });
    
    return () => {
      unsubscribe();
      client.unsubscribe(['activity']);
    };
  }, [client, isConnected]);
  
  return trades;
}

// Hook to listen for WebSocket invalidation events and invalidate React Query cache
// This should be used at the app root level with access to QueryClient
export function useWebSocketInvalidationListener(queryClient: { invalidateQueries: (options: { queryKey: string[] }) => void }) {
  useEffect(() => {
    const handlePositionUpdate = () => {
      queryClient.invalidateQueries({ queryKey: ['positions'] });
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      // Also invalidate the tradeclaw closed positions query so the Closed tab
      // refreshes immediately when a position is closed via WebSocket
      queryClient.invalidateQueries({ queryKey: ['tradeclaw', 'closedPositions'] });
    };
    
    const handlePortfolioUpdate = () => {
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    };
    
    const handleTradeUpdate = () => {
      queryClient.invalidateQueries({ queryKey: ['trades'] });
      queryClient.invalidateQueries({ queryKey: ['activity'] });
      queryClient.invalidateQueries({ queryKey: ['positions'] });
    };
    
    window.addEventListener('ws:position:update', handlePositionUpdate);
    window.addEventListener('ws:portfolio:update', handlePortfolioUpdate);
    window.addEventListener('ws:trade:update', handleTradeUpdate);
    
    return () => {
      window.removeEventListener('ws:position:update', handlePositionUpdate);
      window.removeEventListener('ws:portfolio:update', handlePortfolioUpdate);
      window.removeEventListener('ws:trade:update', handleTradeUpdate);
    };
  }, [queryClient]);
}

// Higher-order hook that combines WebSocket connection with cache invalidation
export function useWebSocketWithCacheInvalidation() {
  const webSocket = useWebSocket();
  useWebSocketCacheInvalidation();
  return webSocket;
}
